import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";

const IdleContext = createContext();

export const useIdleContext = () => {
  const context = useContext(IdleContext);
  if (!context) {
    throw new Error("useIdleContext must be used within an IdleProvider");
  }
  return context;
};

export const IdleProvider = ({ children }) => {
  const getInitialModalState = () => {
    try {
      const stored = localStorage.getItem("accessPinModalState");
      return stored ? JSON.parse(stored) : false;
    } catch (error) {
      return false;
    }
  };

  const [showIdleModal, setShowIdleModal] = useState(getInitialModalState);

  const lockApp = useCallback((reason = "manual") => {
    localStorage.setItem("accessPinModalState", JSON.stringify(true));
    localStorage.setItem("accessPinModalReason", reason);
    setShowIdleModal(true);
  }, []);

  const triggerIdleTimeout = useCallback(() => {
    const currentState = localStorage.getItem("accessPinModalState");
    if (!currentState || currentState === "false") {
      lockApp("idle");
    }
  }, [lockApp]);

  const hideIdleModal = useCallback(() => {
    localStorage.removeItem("accessPinModalState");
    localStorage.removeItem("accessPinModalReason");
    setShowIdleModal(false);
  }, []);

  // Only sync UNLOCK across tabs. Do not force-lock this tab when another
  // tab goes idle — that made the PIN appear while the user was still working.
  useEffect(() => {
    const syncUnlockFromOtherTabs = (event) => {
      if (event.key !== "accessPinModalState") return;
      // Cleared / false in another tab → unlock here too
      if (event.newValue == null || event.newValue === "false") {
        setShowIdleModal(false);
      }
    };

    window.addEventListener("storage", syncUnlockFromOtherTabs);
    return () => {
      window.removeEventListener("storage", syncUnlockFromOtherTabs);
    };
  }, []);

  const value = useMemo(
    () => ({
      showIdleModal,
      lockApp,
      triggerIdleTimeout,
      hideIdleModal,
    }),
    [showIdleModal, lockApp, triggerIdleTimeout, hideIdleModal],
  );

  return (
    <IdleContext.Provider value={value}>{children}</IdleContext.Provider>
  );
};
